import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Breadcrumb, BreadcrumbItem, Container, Form, Label, Row, Spinner } from "reactstrap";
import { useSetRecoilState } from "recoil";
import { CustomLink } from "src/components/Common/CustomLink";
import FormInput from "src/components/Common/Form/FormInput";
import FormTextarea from "src/components/Common/Form/FormTextarea";
import FormUpload from "src/components/Common/Form/FormUpload";
import { navigationPromptState } from "src/recoil/atoms/global";
import APP_ROUTES from "src/routes/routes";
import { FORM_NAME } from "../new";
import { FILE_TYPE, UPLOAD_TYPE } from "src/constant/common";
import { useTranslation } from "react-i18next";
import { usePatchedNavigate } from "src/Hooks/usePatchedNavigate";

const CreateInstanceForm = ({ submitData, data, title, getRedirectURL = () => APP_ROUTES.PROJECT_INSTANCE }) => {
  const { t } = useTranslation("project-instance");
  const navigate = usePatchedNavigate();
  const navigationPrompt = useSetRecoilState(navigationPromptState);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isUpload, setIsUpload] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    watch,
    clearErrors,
  } = useForm({
    defaultValues: data,
  });

  const watchMissionDatabase = watch(FORM_NAME.MISSION_DATABASE);
  const watchDashboardOpi = watch(FORM_NAME.DASHBOARD_OPI);

  const handleSubmitForm = (data) => {
    navigationPrompt(false);
    setIsSubmit(true);
    const formData = {
      [FORM_NAME.NAME]: data[FORM_NAME.NAME].trim() || "",
      [FORM_NAME.DESCRIPTION]: data[FORM_NAME.DESCRIPTION].trim() || "",
      [FORM_NAME.MISSION_DATABASE]: data[FORM_NAME.MISSION_DATABASE]?.[0] || "",
      [FORM_NAME.DASHBOARD_OPI]: data[FORM_NAME.DASHBOARD_OPI]?.[0] || "",
    };
    return submitData({ data: formData }, (isSubmitFailed) => {
      setIsSubmit(false);
      isSubmitFailed && isDirty && navigationPrompt(true);
    });
  };

  const handleUpdateAttachmentsFile = (file, fieldName) => {
    navigationPrompt(true);
    setValue(fieldName, file);
    if (watch(fieldName)) {
      clearErrors(fieldName);
    }
  };

  useEffect(() => {
    if (isDirty) {
      navigationPrompt(true);
    }
  }, [isDirty]);

  return (
    <Container fluid className="project-instance-container">
      <Breadcrumb>
        <BreadcrumbItem>
          <CustomLink to={getRedirectURL()} className="link-primary">
            {t("list.title")}
          </CustomLink>
        </BreadcrumbItem>
        <BreadcrumbItem active>{t("new.title_breadcrumb")}</BreadcrumbItem>
      </Breadcrumb>

      <Row>
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="header-title">{title || t("new.title")}</div>
              <Form onSubmit={handleSubmit(handleSubmitForm)} className="create-new-instance-form">
                <div className="row">
                  <div className="col-md-6">
                    <div className="pe-md-5">
                      <FormInput
                        register={register}
                        label={t("new.name")}
                        name={FORM_NAME.NAME}
                        placeholder={t("new.name_placeholder")}
                        errors={errors?.[FORM_NAME.NAME]?.message}
                        requiredMessage={t("field_required", { field_name: t("new.name") })}
                        required
                        maxLength={255}
                        maxLengthMessage={t("maxlength")}
                      />
                      <FormTextarea
                        register={register}
                        label={t("new.description")}
                        name={FORM_NAME.DESCRIPTION}
                        placeholder={t("new.description_placeholder")}
                        errors={errors?.[FORM_NAME.DESCRIPTION]?.message}
                        requiredMessage={t("field_required", { field_name: t("new.description") })}
                        classInput="height-by-px-129"
                        rows={4}
                        required
                        maxLength={2000}
                        maxLengthMessage={t("textarea_maxlength")}
                      />
                    </div>
                  </div>

                  <div className="col-md-6 mb-4">
                    <Label className="position-relative">{t(`new.${FORM_NAME.MISSION_DATABASE}`)}</Label>
                    <FormUpload
                      register={register}
                      errors={errors}
                      files={watchMissionDatabase}
                      name={FORM_NAME.MISSION_DATABASE}
                      fileTypeAllow={[FILE_TYPE.XLS]}
                      fileTypeValidateMsg="validate_attachments"
                      handleUpdateAttachments={(file) => handleUpdateAttachmentsFile(file, FORM_NAME.MISSION_DATABASE)}
                      onUpload={setIsUpload}
                      isMultiple={false}
                      uploadType={UPLOAD_TYPE.MDB}
                    />
                    <Label className="position-relative">{t(`new.${FORM_NAME.DASHBOARD_OPI}`)}</Label>
                    <FormUpload
                      register={register}
                      errors={errors}
                      files={watchDashboardOpi}
                      name={FORM_NAME.DASHBOARD_OPI}
                      fileTypeAllow={[FILE_TYPE.OPI]}
                      fileTypeValidateMsg="validate_attachments"
                      handleUpdateAttachments={(file) => handleUpdateAttachmentsFile(file, FORM_NAME.DASHBOARD_OPI)}
                      onUpload={setIsUpload}
                      isMultiple={false}
                      uploadType={UPLOAD_TYPE.OPI}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-center gap-2 mt-2">
                  <span
                    role="button"
                    className="btn btn-secondary width-by-px-200 height-by-px-38"
                    onClick={() => navigate(getRedirectURL())}
                  >
                    <span>{t("cancel")}</span>
                  </span>
                  <button className="btn btn-primary width-by-px-200 height-by-px-38" disabled={isSubmit || isUpload} type="submit">
                    {isSubmit ? <Spinner size="sm" /> : <span>{t("submit")}</span>}
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </Row>
    </Container>
  );
};

export default CreateInstanceForm;
